import 'leaflet/dist/leaflet.css';
import 'leaflet-polylinedecorator';

import { useContext, useEffect, useRef, useState } from 'react';
import { MapContainer, TileLayer, useMapEvents, ZoomControl } from 'react-leaflet';

import { useTargetTracking } from '@/hooks/useTargetTracking'; // FIXME @/hooks
import { SaveMapPositionComponent } from './SaveMapPositionComponent';
import { RoutesBoundaryComponent } from './RoutesBoundaryComponent';
import { TrackingBoundaryComponent } from './TrackingBoundaryComponent';
import { TargetMarkers } from './TargetMarkers';
import { ctxPluto } from '@/contexts/PlutoContext';
import { Routes } from './Route';
import { useRouteLoader } from '@/hooks/useRouteLoader';
import { YouAreHere } from './YouAreHere';
import { LoadTargetToken } from './LoadTargetToken';
import { isPhoneOrHighScreen, isRunningInApp } from '@/utilities/sizeUtils';
import { ctxRoutes } from '@/contexts/RoutesCtx';
import { TrackedPathPolyline } from './TrackedPathPolyline';
import CreateRoutesPanes from './CreateRoutesPanes';
import TargetTrackingPanel from '@/components/tracking/TargetTrackingPanel';
import { addBreadcrumb, reportError } from '@/integrations/sentry/errorReporting';
import { requestPlatformInformation } from '@/components/flutter/DeviceInfo';

import './Map.css';
import { LatLng, LeafletEvent, TileErrorEvent } from 'leaflet';
import LayersControl from '@/components/goi/LayersControl';
import GoiVisualizer from '@/components/goi/GoiVisualizer';
import { MapContext } from '@/contexts/MapContext';

import './Map.css';
import { useUserPreferencesState } from '@/hooks/useUserPreferencesState';
import { Box, Link, Typography } from '@mui/material';
import { ctxUser } from '@/contexts/UserContext';

const MAP_ZOOM_LEVEL_PREFERENCES_KEY = 'map.zoomLevel';
const DEFAULT_MAP_ZOOM_LEVEL = 7;
const MAP_CENTER_POSITION_PREFERENCES_KEY = 'map.centerPosition';
const DEFAULT_MAP_CENTER_POSITION = new LatLng(47.162494, 19.503304);
const MAP_MAX_NATIVE_ZOOM_LEVEL = 18;
const MAP_MAX_SCALE_ZOOM_LEVEL = 22;

function MapInformationBar({ isPhone }: { isPhone: boolean }) {
  const runningInApp = isRunningInApp();
  const [appVersion, setAppVersion] = useState<string>();
  const userCtx = useContext(ctxUser);

  useEffect(() => {
    if (!runningInApp) {
      return;
    }

    let active = true;
    void requestPlatformInformation()
      .then((platformInformation) => {
        if (active) {
          setAppVersion(platformInformation.app.version);
        }
      })
      .catch((error) => {
        reportError(error ?? new Error('Failed to request platform information'), {
          feature: 'map',
          operation: 'requestPlatformInformation',
        });
      });

    return () => {
      active = false;
    };
  }, [runningInApp]);

  return (
    <Box
      data-qa-element-id="map_information_bar"
      position="absolute"
      zIndex={500}
      right={0}
      bottom={0}
      left={0}
      display="flex"
      alignItems="center"
      gap={1}
      boxSizing="border-box"
      minWidth={0}
      px={0.625}
      bgcolor="background.paper"
      color="primary.main"
      sx={{ pointerEvents: 'none', opacity: 0.82 }}
    >
      <Typography sx={{ minWidth: 0, flex: '1 1 auto' }} variant="helperText" color="primary" noWrap>
        {VERSION} {appVersion ? `(${appVersion})` : ''} | {userCtx?.user.name}
      </Typography>
      <Box
        component="span"
        flex="0 0 auto"
        whiteSpace="nowrap"
        fontSize={isPhone ? '0.625rem' : undefined}
        lineHeight={isPhone ? 1 : undefined}
        sx={{ pointerEvents: 'auto' }}
      >
        <Link href="https://leafletjs.com" title="A JavaScript library for interactive maps">
          Leaflet
        </Link>{' '}
        &copy; <Link href="https://www.openstreetmap.org/copyright">OSM</Link>
      </Box>
    </Box>
  );
}

export default function PlutoMap() {
  const [isPhone, setPhone] = useState<boolean>(isPhoneOrHighScreen);
  const ctx = useContext(ctxPluto);
  const routesCtx = useContext(ctxRoutes);
  // csak az egyik kell....
  const [highlightedRoutes, grayRoutes] = useRouteLoader();
  const fullTargets = useTargetTracking();
  const [selectedTargetId, setSelectedTargetId] = useState<number | undefined>();
  const routesMapRef = useRef<L.Map | null>(null);
  const targetMapRef = useRef<L.Map | null>(null);
  const [mapZoomLevel, setMapZoomLevel] = useUserPreferencesState<number>(
    MAP_ZOOM_LEVEL_PREFERENCES_KEY,
    DEFAULT_MAP_ZOOM_LEVEL
  );
  const [mapCenterPosition, setMapCenterPosition] = useUserPreferencesState<LatLng>(
    MAP_CENTER_POSITION_PREFERENCES_KEY,
    DEFAULT_MAP_CENTER_POSITION
  );
  const [routesZoomLevel, setRoutesZoomLevel] = useState(DEFAULT_MAP_ZOOM_LEVEL);

  function RoutesZoomHandler() {
    // eslint-disable-next-line react-compiler/react-compiler
    const map = useMapEvents({
      zoomend: () => {
        setRoutesZoomLevel(map.getZoom());
      },
    });
    // eslint-disable-next-line react-compiler/react-compiler
    useEffect(() => {
      if (!map) return;
      setRoutesZoomLevel(map.getZoom());
    }, [map]);
    return null;
  }

  // ez jojjon kivulrol
  useEffect(() => {
    function handleResize() {
      setPhone(isPhoneOrHighScreen);
    }

    // TODO lehetne mediaquery
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    ctx?.setFullTargets(fullTargets); // FIXME lehet inkabb a hook csinalja ?
  }, [ctx, fullTargets]);

  useEffect(() => {
    if (!ctx || !targetMapRef.current) return;
    if (isPhoneOrHighScreen() && ctx.popupTargetId) {
      if (ctx.isBottomSheetOpen) {
        targetMapRef.current.getContainer().style.height = 'calc(100% - 260px)';
      } else {
        targetMapRef.current.getContainer().style.height = 'calc(100% - 43px)';
      }
    } else targetMapRef.current.getContainer().style.height = '100%';
  }, [ctx, ctx?.popupTargetId]);

  useEffect(() => {
    if (!routesCtx || !routesMapRef.current) return;
    if (isPhoneOrHighScreen() && routesCtx.isBottomSheetOpen) {
      if (routesCtx.isBottomSheetUp) {
        routesMapRef.current.getContainer().style.height = 'calc(100% - 260px)';
      } else {
        routesMapRef.current.getContainer().style.height = 'calc(100% - 43px)';
      }
    } else routesMapRef.current.getContainer().style.height = '100%';
  }, [routesCtx, routesCtx?.isBottomSheetUp, routesCtx?.isBottomSheetOpen]);

  let trackedPath = undefined;
  if (fullTargets) {
    const popupFullTarget = fullTargets.find((entry) => entry.target.id === ctx?.popupTargetId);
    trackedPath = popupFullTarget?.trackedPath;
  }

  const handleTileError = (event: TileErrorEvent) => {
    addBreadcrumb('Leaflet tile failed to load', {
      category: 'map.tile',
      level: 'warning',
      data: {
        coords: { x: event.coords.x, y: event.coords.y, z: event.coords.z },
        error: event.error.message,
        src: event.tile.src,
      },
    });
  };

  const handleError = (e: LeafletEvent) => {
    addBreadcrumb('Leaflet map layer error', {
      category: 'map',
      level: 'warning',
      data: { eventType: e.type },
    });
  };

  return (
    <MapContext>
      <>
        {ctx?.mode === 'route' && (
          <div className="map" data-qa-element-id="map_panel">
            <MapContainer
              className={`map${isPhone ? ' phone' : ''}`}
              ref={routesMapRef}
              attributionControl={false}
              center={mapCenterPosition}
              zoom={mapZoomLevel}
              scrollWheelZoom={true}
              zoomControl={false}
              style={{ zIndex: 1 }}
            >
              <CreateRoutesPanes />
              <TileLayer
                url="/osm/{z}/{x}/{y}.png"
                maxNativeZoom={MAP_MAX_NATIVE_ZOOM_LEVEL}
                maxZoom={MAP_MAX_SCALE_ZOOM_LEVEL}
                eventHandlers={{
                  error: handleError,
                  tileerror: handleTileError,
                }}
              />
              <MapInformationBar isPhone={isPhone} />
              {!isPhoneOrHighScreen() && <ZoomControl position="bottomright" />}
              <Routes
                highlightedRoutes={highlightedRoutes}
                grayRoutes={grayRoutes}
                zoomSize={Math.pow(10, -(routesZoomLevel - 5))}
              />
              <RoutesBoundaryComponent fullRoutes={highlightedRoutes} />
              <RoutesZoomHandler />
              <LayersControl />
              <GoiVisualizer />
            </MapContainer>
          </div>
        )}
        {ctx?.mode === 'target' && (
          <>
            {!isPhone && <TargetTrackingPanel activeMenuIndex={'1'} />}
            <div className="map" data-qa-element-id="map_panel">
              <MapContainer
                className={`map${isPhone ? ' phone' : ''}`}
                ref={targetMapRef}
                center={mapCenterPosition}
                zoom={mapZoomLevel}
                preferCanvas
                scrollWheelZoom={true}
                zoomControl={false}
                style={{
                  zIndex: 1,
                  height: ctx.popupTargetId && isPhoneOrHighScreen() ? 'calc(100% - 260px)' : '100%',
                }}
              >
                <SaveMapPositionComponent setZoomLevel={setMapZoomLevel} setCenterPosition={setMapCenterPosition} />
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>'
                  url="/osm/{z}/{x}/{y}.png"
                  maxNativeZoom={MAP_MAX_NATIVE_ZOOM_LEVEL}
                  maxZoom={MAP_MAX_SCALE_ZOOM_LEVEL}
                  eventHandlers={{
                    error: handleError,
                    tileerror: handleTileError,
                  }}
                />
                <MapInformationBar isPhone={isPhone} />
                {!isPhoneOrHighScreen() && <ZoomControl position="bottomright" />}
                <YouAreHere />
                <LoadTargetToken />
                <TargetMarkers
                  fullTargets={fullTargets}
                  selectedTargetId={selectedTargetId}
                  setSelectedTargetId={setSelectedTargetId}
                />
                <TrackingBoundaryComponent
                  fullTargets={fullTargets}
                  selectedTargetId={selectedTargetId}
                  setSelectedTargetId={setSelectedTargetId}
                />
                {trackedPath && trackedPath.length > 0 && ctx.popupTargetId && (
                  <TrackedPathPolyline targetId={ctx.popupTargetId} positions={trackedPath} />
                )}
                <LayersControl />
                <GoiVisualizer />
              </MapContainer>
            </div>
          </>
        )}
      </>
    </MapContext>
  );
}
